import type { ReactNode, ThHTMLAttributes } from 'react';
import { Icon } from './icons';

export interface CollectionTableColumn {
  id: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  className?: string;
  sortable?: boolean;
}

export type CollectionSortDirection = 'asc' | 'desc';

export interface CollectionSortState {
  columnId: string;
  direction: CollectionSortDirection;
}

function getAlignmentClass(alignment: CollectionTableColumn['align']): string {
  switch (alignment) {
    case 'right':
      return 'is-right';
    case 'center':
      return 'is-center';
    default:
      return 'is-left';
  }
}

export function CollectionTable({
  columns,
  children,
  ariaLabel,
  sortState,
  onSortChange
}: {
  columns: CollectionTableColumn[];
  children: ReactNode;
  ariaLabel: string;
  sortState?: CollectionSortState | undefined;
  onSortChange?: (state: CollectionSortState) => void;
}): JSX.Element {
  function handleSort(column: CollectionTableColumn): void {
    if (!column.sortable || !onSortChange) {
      return;
    }

    const nextDirection = sortState?.columnId === column.id && sortState.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({
      columnId: column.id,
      direction: nextDirection
    });
  }

  return (
    <div className="av-collection-table">
      <table aria-label={ariaLabel}>
        <thead>
          <tr>
            {columns.map((column) => (
              <CollectionTableHeaderCell
                key={column.id}
                align={column.align}
                className={column.className}
                sortable={column.sortable}
                sortDirection={sortState?.columnId === column.id ? sortState.direction : null}
                onSort={column.sortable ? () => handleSort(column) : undefined}
              >
                {column.label}
              </CollectionTableHeaderCell>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function CollectionTableHeaderCell({
  align,
  className,
  children,
  sortable = false,
  sortDirection,
  onSort
}: {
  align?: CollectionTableColumn['align'] | undefined;
  className?: string | undefined;
  sortable?: boolean | undefined;
  sortDirection?: CollectionSortDirection | null | undefined;
  onSort?: (() => void) | undefined;
  children: ReactNode;
} & Omit<ThHTMLAttributes<HTMLTableHeaderCellElement>, 'className'>): JSX.Element {
  const ariaSort = sortDirection ? (sortDirection === 'asc' ? 'ascending' : 'descending') : sortable ? 'none' : undefined;

  return (
    <th
      aria-sort={ariaSort}
      className={['av-collection-table__cell', getAlignmentClass(align), sortable ? 'is-sortable' : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
    >
      {sortable ? (
        <button className="av-collection-table__sort-button" type="button" onClick={onSort}>
          <span>{children}</span>
          {sortDirection ? <Icon name={sortDirection === 'asc' ? 'arrowUp' : 'arrowDown'} /> : null}
        </button>
      ) : (
        children
      )}
    </th>
  );
}
